import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { PrefixUrlPipe } from '../../pipes/prefix-url.pipe';

@Component({
  selector: 'eventq-mini-playback-controls',
  imports: [PrefixUrlPipe],
  templateUrl: './mini-playback-controls.html',
  styleUrl: './mini-playback-controls.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqMiniPlaybackControls {
  readonly elapsedLabel = input('0:00');
  readonly durationLabel = input('0:02');
  /** Progress percentage in `0…100`. */
  readonly progress = input(0);
  readonly playing = input(false);
  readonly disabled = input(false);
  /** When false, skip-back / skip-forward are omitted (Standard Clip). Default true. */
  readonly showSkip = input(true);
  /** Seconds each skip moves. Drives both the button labels and the glyph digits. */
  readonly skipSeconds = input(15);
  /** Two digits have to shrink to stay inside the 24px arrow. */
  protected readonly skipGlyphFontSize = computed(() => (this.skipSeconds() >= 10 ? 6 : 7.5));
  /**
   * Optional override for the play glyph. When unset, the built-in SVG from
   * `assets/play_arrow.svg` is rendered inline (no host-app asset path).
   */
  readonly playIconSrc = input<string | null>(null);
  /**
   * Optional override for the scrubber handle. When unset, the built-in SVG from
   * `assets/scrubber-handle.svg` is rendered inline.
   */
  readonly scrubberIconSrc = input<string | null>(null);

  readonly playToggle = output<void>();
  readonly skipBackward = output<void>();
  readonly skipForward = output<void>();
  /** Seek ratio in `0…1` from track click/drag. */
  readonly seek = output<number>();

  private scrubbing = false;

  onTrackPointerDown(event: PointerEvent): void {
    if (this.disabled() || event.button !== 0) {
      return;
    }
    const track = event.currentTarget as HTMLElement;
    this.scrubbing = true;
    track.setPointerCapture?.(event.pointerId);
    this.emitSeekFromClientX(track, event.clientX);
    event.preventDefault();
  }

  onTrackPointerMove(event: PointerEvent): void {
    if (!this.scrubbing) {
      return;
    }
    this.emitSeekFromClientX(event.currentTarget as HTMLElement, event.clientX);
  }

  onTrackPointerUp(event: PointerEvent): void {
    if (!this.scrubbing) {
      return;
    }
    this.scrubbing = false;
    const track = event.currentTarget as HTMLElement;
    if (track.hasPointerCapture?.(event.pointerId)) {
      track.releasePointerCapture?.(event.pointerId);
    }
  }

  private emitSeekFromClientX(track: HTMLElement, clientX: number): void {
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    this.seek.emit(ratio);
  }
}
